import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { User, MapPin, BookOpen, Repeat, ArrowLeft, MessageSquareQuote, Calendar } from "lucide-react";
import api from "../lib/api";
import Stars, { RatingLabel } from "../components/Stars";
import BookCard from "../components/BookCard";
import BookCover from "../components/BookCover";
import ShareMenu from "../components/ShareMenu";
import QRDialog from "../components/QRDialog";

export default function PublicProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get(`/users/${id}/reviews`); setData(res.data); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="max-w-2xl mx-auto px-4 py-20 text-center text-muted-foreground">Caricamento…</div>;
  if (!data) return <div className="max-w-2xl mx-auto px-4 py-20 text-center">Utente non trovato.</div>;

  const { user, rating, active_books, completed_exchanges, items, books } = data;
  const since = user.created_at ? new Date(user.created_at).toLocaleDateString("it-IT", { month: "long", year: "numeric" }) : null;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <button onClick={() => navigate(-1)} className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="w-4 h-4 mr-2" />Indietro</button>

      <div className="rounded-2xl border border-border bg-card p-6" data-testid="public-profile">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0"><User className="w-8 h-8" /></div>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-serif font-bold truncate">{user.name}</h1>
            <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{user.comune}</p>
            <div className="mt-1.5"><RatingLabel rating={rating} /></div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <ShareMenu
              text={`Guarda il profilo di ${user.name} su Libri in Circolo: ${typeof window !== "undefined" ? window.location.href : ""}`}
              align="end" testid="profile-share" />
            <QRDialog
              url={typeof window !== "undefined" ? window.location.href : ""}
              title={`profilo-${user.name}`}
              heading={`Profilo di ${user.name}`}
              description="Mostra o stampa questo QR: chi lo inquadra apre il profilo pubblico con i libri offerti." />
          </div>
        </div>

        {books && books.length > 0 && (
          <div className="mt-5 flex gap-2 overflow-x-auto pb-1" data-testid="cover-gallery">
            {books.slice(0, 8).map((b) => (
              <a key={b.id} href={`/libro/${b.id}`} className="w-14 aspect-[3/4] rounded-md overflow-hidden shrink-0 border border-border hover:opacity-90 transition-opacity">
                <BookCover book={b} />
              </a>
            ))}
          </div>
        )}

        <div className="grid grid-cols-3 gap-3 mt-6">
          <div className="rounded-xl bg-muted/50 p-3 text-center">
            <BookOpen className="w-4 h-4 mx-auto text-muted-foreground mb-1" />
            <p className="text-xl font-serif font-bold">{active_books}</p>
            <p className="text-[11px] text-muted-foreground">Libri attivi</p>
          </div>
          <div className="rounded-xl bg-muted/50 p-3 text-center">
            <Repeat className="w-4 h-4 mx-auto text-muted-foreground mb-1" />
            <p className="text-xl font-serif font-bold">{completed_exchanges}</p>
            <p className="text-[11px] text-muted-foreground">Scambi conclusi</p>
          </div>
          <div className="rounded-xl bg-muted/50 p-3 text-center">
            <Calendar className="w-4 h-4 mx-auto text-muted-foreground mb-1" />
            <p className="text-sm font-medium mt-1 capitalize">{since || "—"}</p>
            <p className="text-[11px] text-muted-foreground">Iscritto da</p>
          </div>
        </div>
      </div>

      <h2 className="text-lg font-serif font-semibold mt-8 mb-3 flex items-center gap-2"><BookOpen className="w-5 h-5" />Libri offerti ({books?.length || 0})</h2>
      {(!books || books.length === 0) ? (
        <div className="text-center py-10 rounded-2xl border border-dashed border-border text-muted-foreground">Nessun libro attualmente offerto.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="profile-books">
          {books.map((b) => <BookCard key={b.id} book={b} />)}
        </div>
      )}

      <h2 className="text-lg font-serif font-semibold mt-8 mb-3 flex items-center gap-2"><MessageSquareQuote className="w-5 h-5" />Recensioni ({items.length})</h2>
      {items.length === 0 ? (
        <div className="text-center py-12 rounded-2xl border border-dashed border-border text-muted-foreground">Ancora nessuna recensione.</div>
      ) : (
        <div className="space-y-3">
          {items.map((r, i) => (
            <div key={r.id || i} data-testid="profile-review" className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-sm">{r.reviewer_name}</span>
                <Stars value={r.rating} size={14} />
              </div>
              {r.comment && <p className="text-sm text-muted-foreground mt-2">"{r.comment}"</p>}
              <p className="text-xs text-muted-foreground mt-2">{new Date(r.created_at).toLocaleDateString("it-IT")}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
